import type {
  WorkflowRunNodeDetail,
  WorkflowRunNodeStatus,
} from '@/apis/logiblocksAPI';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { NodeRunReport } from '@/proto/logiblocks/node_run_report';
import { getNodeDisplayName, requireLBNodeInfo } from '../nodes/registry';
import type { ReactFlowNode } from '../types';
import type { RunGraphNotice, RunView } from './useRunView';

/** One node run in a run's step list. */
export interface RunStep {
  nodeId: string;
  /** The node's data in the run's graph, or null if the run's graph has no such node. */
  nodeData: LBNodeData | null;
  status: WorkflowRunNodeStatus;
  /** When the node started, as UTC ISO-8601. */
  startedAt: string;
  /** How long the node ran, or null while it is still running. */
  durationMs: number | null;
  /** What the node did, or null if it stored no report. */
  report: NodeRunReport | null;
}

/**
 * Returns one step per row of a run's node detail, in the rows' order, with each node's data from
 * the graph the run executed.
 */
export function buildRunSteps(
  rows: WorkflowRunNodeDetail[],
  runGraphNodes: ReactFlowNode[]
): RunStep[] {
  const nodesById = new Map<string, ReactFlowNode>();
  for (const node of runGraphNodes) {
    nodesById.set(node.id, node);
  }
  const steps: RunStep[] = [];
  for (const row of rows) {
    const node = nodesById.get(row.node_id);
    if (node === undefined) {
      console.error(
        `Run has a step for node ${row.node_id}, which its graph does not hold`
      );
    }
    steps.push({
      nodeId: row.node_id,
      nodeData: node === undefined ? null : node.data,
      status: row.status,
      startedAt: row.started_at,
      durationMs: row.completed_at === null ? null : row.duration_ms,
      report: row.report,
    });
  }
  return steps;
}

/** Returns whether a run with `notice` has a graph to draw, or will once its graph loads. */
export function canShowRunGraph(notice: RunGraphNotice): boolean {
  switch (notice) {
    case null:
    case 'older':
      return true;
    case 'missing':
    case 'unavailable':
    case 'load-failed':
      return false;
    default:
      console.error(`Unexpected run graph notice: ${String(notice)}`);
      return false;
  }
}

/**
 * Returns the viewed run's steps, or null until its detail and graph have loaded, or if its graph
 * can't be shown, because the steps couldn't be named.
 */
export function getViewedRunSteps(runView: RunView): RunStep[] | null {
  const { runDetail, viewedRunId, runDisplay, isRunGraphLoading } = runView;
  if (
    isRunGraphLoading ||
    runDetail === null ||
    runDetail.id !== viewedRunId ||
    runDisplay === null ||
    !canShowRunGraph(runDisplay.notice)
  ) {
    return null;
  }
  return buildRunSteps(runDetail.nodes, runDisplay.graph.nodes);
}

/** Returns the step's node's display name in the run's graph, or its node id if the graph lacks it. */
export function getRunStepName(step: RunStep): string {
  if (step.nodeData === null) {
    return step.nodeId;
  }
  return getNodeDisplayName(step.nodeData, requireLBNodeInfo(step.nodeData));
}
